import Lenis from 'lenis'
import 'lenis/dist/lenis.css'
import { motionOn } from './motion.ts'

/**
 * Smooth page scrolling with Lenis (https://github.com/darkroomengineering/lenis, MIT) for the mouse wheel, trackpad
 * and programmatic jumps (docs/UX-PLAYBOOK.md §8). Touch scrolling stays native (`syncTouch: false`, Lenis' own advice:
 * the phone's momentum feels best). Nested scrollers (Mutiara feed, sheets) carry `data-lenis-prevent`.
 * Off with reduced motion and in jsdom (tests); every helper then falls back to the browser API.
 */
let lenis: Lenis | null = null
let locks = 0
/** jsdom has no layout and only logs "not implemented" for window.scrollTo */
const realBrowser = () => !/jsdom/i.test(navigator.userAgent)

export function startSmoothScroll() {
  if (lenis || !motionOn() || !realBrowser()) return
  lenis = new Lenis({ autoRaf: true, lerp: 0.1, syncTouch: false })
}

/**
 * Scrolls `el` into view, like `scrollIntoView`. A sticky bar is kept clear with the element's CSS `scroll-margin-top`,
 * which both paths honour (Lenis subtracts it itself).
 */
export function scrollToEl(el: HTMLElement, { block = 'start', smooth = true }: { block?: 'start' | 'center' | 'end'; smooth?: boolean } = {}) {
  if (lenis) {
    // content may have just grown (a page of verses rendered): without this Lenis clamps to the old page height
    lenis.resize()
    const h = el.getBoundingClientRect().height
    const margin = Number.parseFloat(getComputedStyle(el).scrollMarginTop) || 0
    const off = block === 'center' ? margin - Math.max(0, (window.innerHeight - h) / 2) : block === 'end' ? margin + h - window.innerHeight : 0
    lenis.scrollTo(el, { offset: off, immediate: !smooth, force: true })
    return
  }
  el.scrollIntoView?.({ block, behavior: smooth && motionOn() ? 'smooth' : 'auto' })
}

/** New page: start at the top, without animation. */
export function scrollToTop() {
  if (lenis) lenis.scrollTo(0, { immediate: true, force: true })
  else if (realBrowser()) window.scrollTo(0, 0)
}

/** While a sheet or overlay is open the page behind it stays put. Nested locks are counted; call the result to release. */
export function lockScroll() {
  if (locks++ === 0) lenis?.stop()
  let released = false
  return () => {
    if (released) return
    released = true
    if (--locks === 0) lenis?.start()
  }
}
